import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useArticles } from "../lib/hooks";
import { Reveal, Overline } from "./Reveal";

/** Shows the 3 most relevant Insights articles for the given keywords
 *  (town/service words matched against title + excerpt), newest first as tiebreak. */
export default function RelatedInsights({ keywords = [], title = "From the Insights Journal", dark = false }) {
  const { data: articles } = useArticles();
  // Never surface noindexed articles (e.g. consolidated Devon posts) in internal promotion
  const list = (articles || [])
    .filter((a) => !a.noindex)
    .map((a) => {
      const hay = `${a.title || ""} ${a.excerpt || ""}`.toLowerCase();
      const score = keywords.reduce((s, k) => s + (k && hay.includes(k.toLowerCase()) ? 1 : 0), 0);
      return { a, score };
    })
    .sort((x, y) => y.score - x.score || String(y.a.updated_at).localeCompare(String(x.a.updated_at)))
    .slice(0, 3);
  if (!list.length) return null;

  return (
    <section className={dark ? "bg-obsidian" : "bg-cream"} data-testid="related-insights">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <Overline light={dark}>[ Insights ]</Overline>
            <h2 className={`font-display font-light text-3xl md:text-4xl tracking-tight mt-3 ${dark ? "text-white" : "text-graphite"}`}>
              {title}
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid="related-insights-all"
            className={`hidden sm:inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 shrink-0 ${dark ? "text-white/60 hover:text-white" : "text-graphite/50 hover:text-graphite"}`}
          >
            All articles <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {list.map(({ a }, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <Link to={`/insights/${a.slug}`} data-testid={`related-article-${a.slug}`} className="group block">
                <div className="overflow-hidden aspect-[16/10] bg-stone">
                  <img
                    src={a.cover}
                    alt={a.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <h3 className={`font-display font-light text-xl tracking-tight mt-5 leading-snug transition-colors duration-300 group-hover:text-ember ${dark ? "text-white" : "text-graphite"}`}>
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className={`text-sm leading-relaxed mt-3 line-clamp-2 ${dark ? "text-white/50" : "text-graphite/60"}`}>
                    {a.excerpt}
                  </p>
                )}
                <span className={`inline-flex items-center gap-2 mt-4 font-mono text-[10px] tracking-[0.2em] uppercase ${dark ? "text-white/40" : "text-graphite/40"} group-hover:text-ember transition-colors duration-300`}>
                  Read article <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
